import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Award, Trophy, CheckCircle, MessageCircle, Loader2, Gift, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/use-window-size";
import { useCelebration } from "@/hooks/useCelebration";
import { useToast } from "@/hooks/use-toast";
import { useQuickCertificateShare } from "@/hooks/useQuickCertificateShare";
import { useConnections } from "@/hooks/useConnections";
import { ClaimDrawer } from "./ClaimDrawer";
import { acquirePopup, releasePopup, cancelPopup, POPUP_PRIORITIES } from "@/lib/popupQueue";
import { logger } from "@/utils/logger";

const POPUP_ID = "celebration";

export function GlobalCelebrationModal() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { width, height } = useWindowSize();
  const { isOpen, celebrationData, closeCelebration } = useCelebration();
  const { quickShare, isSharing } = useQuickCertificateShare();
  const { connections } = useConnections();
  const hasConnections = (connections?.length ?? 0) > 0;
  const [showConfetti, setShowConfetti] = useState(false);
  const [claimDrawerOpen, setClaimDrawerOpen] = useState(false);
  const [canShow, setCanShow] = useState(false);
  const [currentDate] = useState(new Date().toLocaleDateString());

  useEffect(() => {
    if (isOpen) {
      let cancelled = false;
      acquirePopup(POPUP_ID, POPUP_PRIORITIES.celebration).then(() => {
        if (!cancelled) {
          setCanShow(true);
          setShowConfetti(true);
        } else {
          releasePopup(POPUP_ID);
        }
      });
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => {
        cancelled = true;
        cancelPopup(POPUP_ID);
        clearTimeout(timer);
      };
    } else {
      setCanShow(false);
      releasePopup(POPUP_ID);
    }
  }, [isOpen]);

  const handleClose = () => {
    closeCelebration();
    toast({
      title: "Achievement saved!",
      description: "You can generate a certificate anytime from Completed Goals.",
      duration: 4000,
    });
  };

  const handleGenerateCertificate = () => {
    closeCelebration();
    navigate("/certificate/new", { 
      state: { 
        goalTitle: celebrationData?.goalTitle,
        preSelectedType: false
      } 
    });
  };

  const handleQuickShare = async () => {
    if (!celebrationData) return;
    const ok = await quickShare({ goalTitle: celebrationData.goalTitle });
    if (ok) closeCelebration();
  };

  const handleShareWithFriends = async () => {
    if (!celebrationData) return;
    const text = `I just completed "${celebrationData.goalTitle}" on GoalDigger! 🎉`;
    const url = window.location.origin;
    try {
      if (navigator.share) {
        await navigator.share({ title: "I hit a goal!", text, url });
      } else {
        await navigator.clipboard?.writeText(`${text} ${url}`);
        toast({ title: "Copied!", description: "Share message copied to clipboard." });
      }
    } catch (e) {
      logger.debug("Share cancelled or failed", e);
    }
  };



  const handleDontAskAgain = () => {
    localStorage.setItem("certificate-prompt-preference", "never");
    closeCelebration();
    toast({
      title: "Preference saved",
      description: "You can still generate certificates from Completed Goals anytime.",
      duration: 3000,
    });
  };

  // Check if user has disabled celebration prompts
  const preference = localStorage.getItem("certificate-prompt-preference");
  if (preference === "never" && isOpen) {
    // Auto-close without showing
    closeCelebration();
    return null;
  }

  if (!celebrationData) return null;

  return (
    <Dialog open={isOpen && canShow} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md animate-scale-in">
        {showConfetti && <Confetti width={width} height={height} recycle={false} numberOfPieces={200} />}
        
        <DialogHeader className="flex flex-col items-center text-center space-y-4">
          <div className="rounded-full bg-primary/10 p-4 text-primary">
            <Trophy className="h-10 w-10" />
          </div>
          <DialogTitle className="text-2xl font-bold">
            Goal Complete! 🎉
          </DialogTitle>
        </DialogHeader>
        
        <div className="text-center space-y-6 py-4">
          <div className="space-y-2">
            <p className="text-lg">
              Great job! You've completed:
            </p>
            <p className="font-bold text-xl text-primary">{celebrationData.goalTitle}</p>
          </div>
          
          <div className="bg-muted p-4 rounded-md flex items-center justify-center gap-3">
            <Award className="h-6 w-6 text-yellow-500" />
            <span className="font-bold text-lg">+{celebrationData.pointsEarned} points earned!</span>
          </div>
          
          <div className="text-sm text-muted-foreground">
            <p>Completed on: {currentDate}</p>
          </div>
        </div>
        
        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button
            onClick={() => {
              closeCelebration();
              setClaimDrawerOpen(true);
            }}
            disabled={isSharing}
            className="w-full gap-2 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-bold shadow-md"
            size="lg"
          >
            <Gift className="h-4 w-4" />
            Claim my +{celebrationData.pointsEarned} points
          </Button>
          <Button
            onClick={handleQuickShare}
            disabled={isSharing}
            variant="outline"
            className="w-full gap-2"
          >
            {isSharing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Preparing certificate…
              </>
            ) : (
              <>
                <MessageCircle className="h-4 w-4" />
                Share to WhatsApp
              </>
            )}
          </Button>
          {hasConnections && (
            <Button
              onClick={handleShareWithFriends}
              variant="outline"
              className="w-full gap-2"
            >
              <Users className="h-4 w-4" />
              Share with {connections.length === 1 ? "your circle" : `your circle (${connections.length})`}
            </Button>
          )}
          <Button
            onClick={handleGenerateCertificate}
            disabled={isSharing}
            variant="ghost"
            className="w-full gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            Customize Certificate
          </Button>
          <button
            onClick={handleDontAskAgain}
            className="text-xs text-muted-foreground hover:text-foreground underline mt-2"
          >
            Don't show this again
          </button>
        </DialogFooter>
      </DialogContent>
      <ClaimDrawer open={claimDrawerOpen} onOpenChange={setClaimDrawerOpen} />
    </Dialog>
  );
}
